/**
 * 🎉 LA FÊTE DE LA VENTE — le moteur, sans React.
 *
 * Kayla, 09-10-2026 : « le plus addictif possible », mais « pas un casino » ;
 * « comme au bowling : des animations de dingue quand tu fais un strike, moins
 * bien quand tu fais des petites ventes » ; et, sur le podium : « ça c'est
 * incroyable, pourquoi tu ne fais pas des trucs du style pour les ventes ? ».
 * Validé sur le banc `ventes-en-fete.html` avant d'arriver ici.
 *
 * Donc : plus de carte. Un OBJET par palier arrive sur une scène sombre (ticket,
 * billet, pièce géante, néon, tableau de bowling, lingot, carte de collection),
 * avec son impact, ses particules et ses sons ; dessous, le fan et le bouton.
 * Le palier suit le montant (`palierDe`) — la part réelle des ventes de la prod
 * (389 ventes, 22-08 → 09-10) est rappelée en face de chaque marche.
 *
 * Le son est SYNTHÉTISÉ (Web Audio) : aucun fichier à télécharger au moment
 * de la vente — c'est ce qui coupait l'ancien son. Il ne joue que si la
 * fenêtre a gagné `reclamerSon` (une seule fenêtre sonne par personne).
 *
 * Un appel = une scène dans `racine` ; la fonction rendue la démonte en entier
 * (minuteries, particules, sons, DOM).
 */
import "./fete.css";

export interface Libelles {
  ouvrir: string;
  fermer: string;
  /** « à {{name}} » déjà résolu, sans le nom : on le complète ici. */
  aQui: (fan: string) => string;
  venteDuJour: (n: number) => string;
  totalDuJour: (montant: string) => string;
}

export interface DonneesVente {
  euros: number;
  /** Symbole (€, $, £) ou code devise. */
  devise: string;
  fan: string | null;
  createur: string | null;
  quoi: string | null;
  /** Ventes enchaînées par CE chatteur (écart ≤ 30 min), celle-ci comprise. */
  combo: number;
  venteDuJour: number | null;
  totalDuJour: string | null;
  peutOuvrir: boolean;
}

export interface DonneesPodium {
  rang: 1 | 2 | 3;
  devant: string | null;
  prime: string | null;
  libelles: { surtitre: string; devant: (nom: string) => string; prime: (montant: string) => string; voir: string; fermer: string };
}

export interface Rappels {
  onOuvrir: () => void;
  onFermer: () => void;
}

/* ═════════════ Les paliers — comme au bowling ═════════════ */

export type Animation = "confettis" | "billets" | "pluie" | "artifice" | "strike" | "jackpot" | "legendaire";

export const PALIERS: { de: number; a: number; anim: Animation[] }[] = [
  { de: 0, a: 20, anim: ["confettis"] }, // 25 % des ventes
  { de: 20, a: 40, anim: ["billets", "pluie"] }, // 41 %
  { de: 40, a: 80, anim: ["artifice"] }, // 28 %
  { de: 80, a: 150, anim: ["strike"] }, // 4 %
  { de: 150, a: 300, anim: ["jackpot"] }, // 1,3 %
  { de: 300, a: Infinity, anim: ["legendaire"] }, // 0,3 %
];

let derniereAnimation: Animation | null = null;

/** L'animation d'un montant. Dans un palier à deux animations, on alterne :
 *  jamais deux fois la même de suite. */
export function animationDe(euros: number): Animation {
  const p = PALIERS.find((x) => euros >= x.de && euros < x.a) ?? PALIERS[0]!;
  const choix = p.anim.length > 1 ? p.anim.find((a) => a !== derniereAnimation) ?? p.anim[0]! : p.anim[0]!;
  derniereAnimation = choix;
  return choix;
}

/** Le nom de chaque animation, en tête de l'objet et de la petite carte. */
const TITRES: Record<Animation, string> = {
  confettis: "Joli coup",
  billets: "Ka-ching",
  pluie: "Pluie d'or",
  artifice: "Quelle vente",
  strike: "Strike",
  jackpot: "Jackpot",
  legendaire: "Légendaire",
};

const NOMS_COMBO = ["", "", "DOUBLÉ", "TURKEY", "EN FEU", "INARRÊTABLE", "LÉGENDE VIVANTE"];
export const nomCombo = (n: number) => (n < 2 ? "" : NOMS_COMBO[Math.min(n, NOMS_COMBO.length - 1)]!);

/* ═════════════ Outils ═════════════ */

const hasard = (a: number, b: number) => a + Math.random() * (b - a);
const choisir = <T,>(l: readonly T[]): T => l[Math.floor(Math.random() * l.length)]!;
const COULEURS = ["#f43f5e", "#f59e0b", "#10b981", "#3b82f6", "#a855f7", "#facc15", "#ec4899", "#22d3ee"];
const N = { G4: 392, C5: 523.25, E5: 659.25, G5: 783.99, C6: 1046.5, E6: 1318.5, G6: 1567.98, C7: 2093 };
const echapper = (t: string) => t.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c]!);
const reduit = () => typeof window !== "undefined" && !!window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

const ICONES = {
  diamant:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round"><path d="M6 3h12l4 6-10 12L2 9z"/><path d="M2 9h20M10 3L8 9l4 12 4-12-2-6"/></svg>',
  fleche:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>',
};

/* ═════════════ Le son ═════════════ */

let AC: AudioContext | null = null;
let sortie: GainNode | null = null;
let bruitBuf: AudioBuffer | null = null;

function contexteAudio(): AudioContext | null {
  if (typeof window === "undefined") return null;
  const C = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!C) return null;
  if (!AC) {
    AC = new C();
    const comp = AC.createDynamicsCompressor();
    comp.threshold.value = -16;
    comp.ratio.value = 4;
    comp.connect(AC.destination);
    sortie = AC.createGain();
    sortie.gain.value = 0.8;
    sortie.connect(comp);
  }
  if (AC.state === "suspended") void AC.resume();
  return AC;
}

/** Une note de fanfare : fréquence, départ (s), durée (s), et l'accord posé dessus. */
type Note = readonly [number, number, number, (readonly number[])?];

/** Les sons d'UNE scène : un bus à elle, coupé net quand la scène s'en va. */
class Son {
  private bus: GainNode | null = null;
  private dernierClink = 0;
  constructor(actif: boolean, volume = 1) {
    const ac = actif ? contexteAudio() : null;
    if (ac && sortie) {
      this.bus = ac.createGain();
      this.bus.gain.value = volume;
      this.bus.connect(sortie);
    }
  }
  get t0() {
    return AC ? AC.currentTime + 0.03 : 0;
  }
  couper() {
    if (!this.bus || !AC) return;
    const b = this.bus;
    this.bus = null;
    b.gain.setTargetAtTime(0, AC.currentTime, 0.04);
    setTimeout(() => b.disconnect(), 400);
  }
  osc(type: OscillatorType, f: number, t: number, d: number, pic: number, o: { vers?: number; a?: number } = {}) {
    if (!this.bus || !AC) return;
    const x = AC.createOscillator(), g = AC.createGain();
    x.type = type;
    x.frequency.setValueAtTime(f, t);
    if (o.vers) x.frequency.exponentialRampToValueAtTime(o.vers, t + d);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(pic, t + (o.a ?? 0.004));
    g.gain.exponentialRampToValueAtTime(0.0001, t + d);
    x.connect(g);
    g.connect(this.bus);
    x.start(t);
    x.stop(t + d + 0.05);
  }
  bruit(t: number, d: number, o: { type?: BiquadFilterType; f?: number; fVers?: number; q?: number; g?: number; a?: number } = {}) {
    if (!this.bus || !AC) return;
    if (!bruitBuf) {
      bruitBuf = AC.createBuffer(1, AC.sampleRate * 2, AC.sampleRate);
      const c = bruitBuf.getChannelData(0);
      for (let i = 0; i < c.length; i++) c[i] = Math.random() * 2 - 1;
    }
    const s = AC.createBufferSource();
    s.buffer = bruitBuf;
    const fl = AC.createBiquadFilter();
    fl.type = o.type ?? "bandpass";
    fl.Q.value = o.q ?? 0.8;
    fl.frequency.setValueAtTime(o.f ?? 1200, t);
    if (o.fVers) fl.frequency.exponentialRampToValueAtTime(o.fVers, t + d);
    const g = AC.createGain();
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(o.g ?? 0.25, t + (o.a ?? 0.002));
    g.gain.exponentialRampToValueAtTime(0.0001, t + d);
    s.connect(fl);
    fl.connect(g);
    g.connect(this.bus);
    s.start(t, Math.random() * 1.5);
    s.stop(t + d + 0.05);
  }
  marimba(t: number, f: number, g = 0.2) {
    this.osc("sine", f, t, 0.55, g);
    this.osc("sine", f * 4, t, 0.09, g * 0.3);
  }
  metal(t: number, f: number, g = 0.05, d = 0.45) {
    [1, 2.76, 5.4, 8.93].forEach((r, i) => this.osc("sine", f * r, t, d * [1, 0.6, 0.4, 0.25][i]!, g * [1, 0.5, 0.3, 0.15][i]!));
    this.bruit(t, 0.015, { type: "highpass", f: 7000, g: g * 1.2 });
  }
  cloche(t: number, f: number, g = 0.07, d = 1.8) {
    [[0.5, 0.5, 1.5], [1, 1, 1], [1.19, 0.6, 0.8], [1.5, 0.5, 0.7], [2, 0.4, 0.6], [2.5, 0.25, 0.5], [3, 0.15, 0.4]].forEach(([r, a, dd]) =>
      this.osc("sine", f * r!, t, d * dd!, g * a!),
    );
  }
  caisse(t: number, k = 1) {
    this.bruit(t, 0.035, { f: 1800, q: 3, g: 0.45 });
    this.bruit(t + 0.055, 0.035, { f: 2500, q: 3, g: 0.4 });
    this.osc("triangle", 150, t + 0.055, 0.08, 0.2);
    this.cloche(t + 0.1, 2093 * k, 0.07, 2);
  }
  compteur(t: number, d = 0.9) {
    for (let x = 0; x < d; x += 0.03) {
      this.bruit(t + x, 0.022, { f: hasard(2600, 3800), q: 2.5, g: 0.12 });
      this.osc("sine", 170, t + x, 0.02, 0.04);
    }
  }
  /** Une note de cuivre : UNE voix (deux désaccordées sonnaient « en double »). */
  cuivre(t: number, f: number, d: number, g = 0.06) {
    if (!this.bus || !AC) return;
    const s = AC.createOscillator(), fl = AC.createBiquadFilter(), v = AC.createGain(), lfo = AC.createOscillator(), lg = AC.createGain();
    s.type = "sawtooth";
    s.frequency.value = f;
    fl.type = "lowpass";
    fl.Q.value = 1.5;
    fl.frequency.setValueAtTime(350, t);
    fl.frequency.linearRampToValueAtTime(3200, t + 0.06);
    fl.frequency.linearRampToValueAtTime(1700, t + d);
    v.gain.setValueAtTime(0.0001, t);
    v.gain.linearRampToValueAtTime(g, t + 0.035);
    v.gain.setValueAtTime(g, t + Math.max(0.04, d - 0.05));
    v.gain.linearRampToValueAtTime(0.0001, t + d + 0.18);
    lfo.frequency.value = 5.6;
    lg.gain.value = f * 0.006;
    lfo.connect(lg);
    lg.connect(s.frequency);
    s.connect(fl);
    fl.connect(v);
    v.connect(this.bus);
    s.start(t);
    lfo.start(t);
    s.stop(t + d + 0.25);
    lfo.stop(t + d + 0.25);
  }
  fanfare(t: number, notes: readonly Note[], k = 1) {
    notes.forEach(([f, o, d, acc]) => {
      this.cuivre(t + o, f * k, d);
      (acc ?? []).forEach((r) => this.cuivre(t + o, f * k * r, d, 0.035));
    });
  }
  pop(t: number) {
    this.bruit(t, 0.09, { type: "highpass", f: 1500, g: 0.55 });
    this.osc("triangle", 200, t, 0.14, 0.35, { vers: 55 });
  }
  sifflet(t: number) {
    this.osc("sine", 750, t, 0.85, 0.028, { vers: 2300 });
    this.bruit(t, 0.85, { f: 3000, fVers: 7500, q: 5, g: 0.03, a: 0.1 });
  }
  boum(t: number, g = 0.55) {
    this.bruit(t, 1.4, { type: "lowpass", f: 2400, fVers: 70, q: 0.3, g });
    this.osc("sine", 78, t, 0.9, 0.45, { vers: 32 });
    for (let i = 0; i < 24; i++) this.bruit(t + 0.15 + Math.random() * 1.0, 0.012, { type: "highpass", f: 3500, g: hasard(0.04, 0.14) });
  }
  roule(t: number, d: number) {
    for (let x = 0; x < d; x += 0.045) this.bruit(t + x, 0.1, { type: "lowpass", f: 160 + x * 600, q: 0.8, g: 0.16 + x * 0.45 });
  }
  quilles(t: number) {
    for (let i = 0; i < 10; i++) {
      const o = t + Math.random() * 0.22, f = hasard(450, 1100);
      this.bruit(o, 0.14, { f, q: 9, g: 0.6 });
      this.osc("triangle", f * 0.5, o, 0.07, 0.12);
    }
    this.bruit(t, 0.6, { f: 1800, q: 0.5, g: 0.3 });
  }
  cymbale(t: number, g = 0.2) {
    this.bruit(t, 2.2, { type: "highpass", f: 5500, g, q: 0.3 });
  }
  roulement(t: number, d: number) {
    let x = 0;
    while (x < d) {
      const p = x / d;
      this.bruit(t + x, 0.07, { type: "highpass", f: 1400, g: 0.07 + 0.22 * p, q: 0.4 });
      this.osc("triangle", 190, t + x, 0.05, 0.03 + 0.08 * p);
      x += 0.085 - 0.062 * p;
    }
  }
  envol(t: number, d = 1.3) {
    for (let i = 0; i < 8; i++) {
      const f = hasard(280, 600);
      this.osc("sine", f, t + i * 0.06, d, 0.02, { vers: f * 4, a: 0.25 });
    }
  }
  choeur(t: number, notes: number[], d = 2.2) {
    if (!this.bus || !AC) return;
    for (const f of notes) {
      const x = AC.createOscillator(), fl = AC.createBiquadFilter(), g = AC.createGain();
      x.type = "sawtooth";
      x.frequency.value = f;
      fl.type = "bandpass";
      fl.frequency.value = 900;
      fl.Q.value = 1.2;
      g.gain.setValueAtTime(0.0001, t);
      g.gain.linearRampToValueAtTime(0.03, t + 0.4);
      g.gain.setValueAtTime(0.03, t + d - 0.5);
      g.gain.linearRampToValueAtTime(0.0001, t + d);
      x.connect(fl);
      fl.connect(g);
      g.connect(this.bus);
      x.start(t);
      x.stop(t + d + 0.05);
    }
  }
  tic(t: number, k = 1) {
    this.osc("sine", 1250 * k, t, 0.05, 0.04);
  }
  ding(t: number, k = 1) {
    this.cloche(t, 1568 * k, 0.05, 1.2);
  }
  clink(k = 1) {
    if (!AC || AC.currentTime - this.dernierClink < 0.035) return;
    this.dernierClink = AC.currentTime;
    this.metal(AC.currentTime + 0.005, hasard(2600, 3600) * k, hasard(0.02, 0.045), 0.35);
  }
}

const FANFARE: Record<"courte" | "moyenne" | "longue", readonly Note[]> = {
  courte: [[N.G4, 0, 0.12], [N.C5, 0.15, 0.7, [1.26, 1.5]]],
  moyenne: [[N.C5, 0, 0.1], [N.C5, 0.13, 0.1], [N.C5, 0.26, 0.1], [N.E5, 0.39, 0.15], [N.G5, 0.56, 0.15], [N.C6, 0.75, 1.0, [1.26, 1.5]]],
  longue: [[N.G4, 0, 0.12], [N.C5, 0.14, 0.12], [N.E5, 0.28, 0.12], [N.G5, 0.42, 0.4], [N.E5, 0.86, 0.12], [N.G5, 1.0, 0.12], [N.C6, 1.16, 1.5, [1.26, 1.5, 2]]],
};

/* ═════════════ La scène : particules, minuteries, DOM ═════════════ */

/* UNE fête à la fois. Le classement se met à jour juste après le paiement :
 * la vente qui fait monter sur le podium déclencherait les deux scènes l'une
 * sur l'autre. La suivante attend que la précédente soit partie. */
let sceneActive: Scene | null = null;
const enAttente: (() => void)[] = [];

function quandLaScene(demarrer: () => () => void): () => void {
  let stop: (() => void) | null = null;
  let annule = false;
  const go = () => {
    if (!annule) stop = demarrer();
  };
  if (sceneActive) enAttente.push(go);
  else go();
  return () => {
    annule = true;
    stop?.();
  };
}

interface Particule {
  type: string;
  x: number;
  y: number;
  vx: number;
  vy: number;
  g: number;
  rot: number;
  vr: number;
  vie: number;
  age: number;
  taille: number;
  frein: number;
  couleur: string;
  phase: number;
  pose?: boolean;
  rebonds?: number;
  maj?: (p: Particule, dt: number) => void;
  fin?: (p: Particule) => void;
}

class Scene {
  readonly el: HTMLDivElement;
  readonly son: Son;
  private cv: HTMLCanvasElement;
  private cx: CanvasRenderingContext2D | null;
  private P: Particule[] = [];
  private raf: number | null = null;
  private avant = 0;
  private minuteries: number[] = [];
  private tas: Record<number, number> = {};
  private finie = false;
  W = 0;
  H = 0;

  constructor(racine: HTMLElement, sonActif: boolean, private onFermer: () => void) {
    this.el = document.createElement("div");
    // `dark` : la scène est toujours sur un voile sombre — les jetons (et donc les boutons de la
    // librairie qu'elle porte) sont ceux de la nuit, quel que soit le thème de la page.
    this.el.className = "fete fete-scene dark";
    this.cv = document.createElement("canvas");
    this.cv.className = "fete-canvas";
    racine.appendChild(this.el);
    racine.appendChild(this.cv);
    let cx: CanvasRenderingContext2D | null = null;
    try {
      cx = this.cv.getContext("2d");
    } catch {
      cx = null; // jsdom : pas de canvas, la scène marche sans particules
    }
    this.cx = cx;
    this.taille();
    window.addEventListener("resize", this.taille);
    this.son = new Son(sonActif);
  }

  private taille = () => {
    const r = Math.min(2, window.devicePixelRatio || 1);
    this.W = window.innerWidth;
    this.H = window.innerHeight;
    this.cv.width = this.W * r;
    this.cv.height = this.H * r;
    this.cx?.setTransform(r, 0, 0, r, 0, 0);
  };

  plus(ms: number, f: () => void) {
    this.minuteries.push(window.setTimeout(f, ms));
  }

  detruire() {
    this.finie = true;
    this.minuteries.forEach(clearTimeout);
    this.minuteries = [];
    if (this.raf !== null) cancelAnimationFrame(this.raf);
    window.removeEventListener("resize", this.taille);
    this.son.couper();
    this.el.remove();
    this.cv.remove();
    if (sceneActive === this) {
      sceneActive = null;
      enAttente.shift()?.();
    }
  }

  /** Fermer en fondu, puis laisser React démonter. */
  fermer = () => {
    if (this.finie) return;
    this.finie = true;
    this.minuteries.forEach(clearTimeout);
    this.el.style.transition = "opacity .25s";
    this.el.style.opacity = "0";
    window.setTimeout(this.onFermer, 260);
  };

  div(cls: string, html?: string): HTMLDivElement {
    const d = document.createElement("div");
    d.className = cls;
    if (html !== undefined) d.innerHTML = html;
    return d;
  }

  voile(cls = "") {
    const v = this.div("fete-voile " + cls);
    v.onclick = this.fermer;
    this.el.appendChild(v);
  }

  secoue() {
    this.el.classList.remove("fete-secousse");
    void this.el.offsetWidth;
    this.el.classList.add("fete-secousse");
  }

  flash() {
    const f = this.div("fete-flash");
    this.el.appendChild(f);
    window.setTimeout(() => f.remove(), 450);
  }

  projecteurs() {
    this.el.appendChild(this.div("fete-projecteur g"));
    this.el.appendChild(this.div("fete-projecteur d"));
  }

  choc(html: string, sous: string | null, cls = ""): HTMLDivElement {
    const b = this.div("fete-intro");
    b.appendChild(this.div("fete-choc " + cls, html));
    if (sous) b.appendChild(this.div("fete-sous-choc", echapper(sous)));
    this.el.appendChild(b);
    return b;
  }

  /* ── particules ── */
  ajoute(p: Partial<Particule> & { type: string }): Particule {
    const q: Particule = { x: 0, y: 0, vx: 0, vy: 0, g: 900, rot: 0, vr: 0, vie: 2, age: 0, taille: 10, frein: 0, couleur: "#fff", phase: Math.random() * 6, ...p };
    if (!this.cx || reduit()) return q;
    this.P.push(q);
    if (this.raf === null) {
      this.avant = performance.now();
      this.raf = requestAnimationFrame(this.image);
    }
    return q;
  }

  private image = (t: number) => {
    const cx = this.cx!;
    const dt = Math.min(0.033, (t - this.avant) / 1000);
    this.avant = t;
    cx.clearRect(0, 0, this.W, this.H);
    for (let i = this.P.length - 1; i >= 0; i--) {
      const p = this.P[i]!;
      p.age += dt;
      if (p.age >= p.vie) {
        this.P.splice(i, 1);
        p.fin?.(p);
        continue;
      }
      p.vy += p.g * dt;
      if (p.frein) {
        p.vx *= 1 - p.frein * dt;
        p.vy *= 1 - p.frein * dt;
      }
      p.maj?.(p, dt);
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.rot += p.vr * dt;
      this.dessine(p);
    }
    if (this.P.length) this.raf = requestAnimationFrame(this.image);
    else {
      this.raf = null;
      this.tas = {};
      cx.clearRect(0, 0, this.W, this.H);
    }
  };

  private dessine(p: Particule) {
    const cx = this.cx!;
    const r = p.age / p.vie;
    cx.save();
    cx.globalAlpha = r > 0.82 ? (1 - r) / 0.18 : 1;
    cx.translate(p.x, p.y);
    const s = p.taille;
    switch (p.type) {
      case "piece":
        this.piece(p, s);
        break;
      case "confetti":
        cx.rotate(p.rot);
        cx.scale(1, Math.cos(p.age * 9 + p.phase));
        cx.fillStyle = p.couleur;
        cx.fillRect(-s / 2, -s / 4, s, s / 2);
        break;
      case "serpentin":
        cx.rotate(p.rot);
        cx.strokeStyle = p.couleur;
        cx.lineWidth = 3;
        cx.lineCap = "round";
        cx.beginPath();
        for (let i = 0; i <= 14; i++) {
          const x = (i - 7) * 3.2, y = Math.sin(i * 0.9 + p.age * 8 + p.phase) * 6;
          if (i) cx.lineTo(x, y);
          else cx.moveTo(x, y);
        }
        cx.stroke();
        break;
      case "ruban":
        cx.rotate(p.rot);
        cx.scale(Math.cos(p.age * 6 + p.phase), 1);
        cx.fillStyle = p.couleur;
        cx.fillRect(-2.5, -s, 5, s * 2);
        break;
      case "etincelle":
        cx.globalCompositeOperation = "lighter";
        cx.fillStyle = p.couleur;
        cx.beginPath();
        cx.arc(0, 0, Math.max(0.3, s * (1 - r * 0.7)), 0, 7);
        cx.fill();
        break;
      case "eclat": {
        cx.globalCompositeOperation = "lighter";
        const k = Math.sin(r * Math.PI) * s;
        cx.fillStyle = "#fff";
        cx.beginPath();
        cx.moveTo(0, -k);
        cx.lineTo(k * 0.18, 0);
        cx.lineTo(0, k);
        cx.lineTo(-k * 0.18, 0);
        cx.closePath();
        cx.fill();
        cx.beginPath();
        cx.moveTo(-k, 0);
        cx.lineTo(0, k * 0.18);
        cx.lineTo(k, 0);
        cx.lineTo(0, -k * 0.18);
        cx.closePath();
        cx.fill();
        break;
      }
      case "billet":
        cx.rotate(p.rot);
        cx.scale(1, 0.55 + 0.45 * Math.cos(p.age * 5 + p.phase));
        cx.fillStyle = "#15803d";
        this.arrondi(-26, -13, 52, 26, 4);
        cx.fill();
        cx.strokeStyle = "#86efac";
        cx.lineWidth = 1.5;
        this.arrondi(-22, -9, 44, 18, 3);
        cx.stroke();
        cx.fillStyle = "#bbf7d0";
        cx.beginPath();
        cx.arc(0, 0, 7, 0, 7);
        cx.fill();
        break;
      case "quille":
        cx.rotate(p.rot);
        cx.fillStyle = "#fafafa";
        cx.strokeStyle = "#94a3b8";
        cx.lineWidth = 1;
        cx.beginPath();
        cx.ellipse(0, 8, 11, 17, 0, 0, 7);
        cx.fill();
        cx.stroke();
        cx.beginPath();
        cx.ellipse(0, -22, 6.5, 7.5, 0, 0, 7);
        cx.fill();
        cx.stroke();
        cx.fillRect(-4, -18, 8, 12);
        cx.fillStyle = "#dc2626";
        cx.fillRect(-4.5, -14, 9, 2.5);
        cx.fillRect(-5, -10, 10, 2.5);
        break;
      case "boule": {
        cx.rotate(p.rot);
        const gb = cx.createRadialGradient(-8, -8, 2, 0, 0, 24);
        gb.addColorStop(0, "#a78bfa");
        gb.addColorStop(0.6, "#5b21b6");
        gb.addColorStop(1, "#1e1b4b");
        cx.fillStyle = gb;
        cx.beginPath();
        cx.arc(0, 0, 24, 0, 7);
        cx.fill();
        cx.fillStyle = "#0f0a24";
        for (const [hx, hy] of [[-6, -8], [3, -11], [-1, -2]] as const) {
          cx.beginPath();
          cx.arc(hx, hy, 3, 0, 7);
          cx.fill();
        }
        break;
      }
      case "fusee":
        cx.globalCompositeOperation = "lighter";
        cx.fillStyle = "#fff7c2";
        cx.beginPath();
        cx.arc(0, 0, 3, 0, 7);
        cx.fill();
        break;
      case "flamme": {
        cx.globalCompositeOperation = "lighter";
        const gf = cx.createRadialGradient(0, 0, 0, 0, 0, s);
        gf.addColorStop(0, "rgba(255,240,180,.9)");
        gf.addColorStop(0.4, "rgba(251,146,60,.7)");
        gf.addColorStop(1, "rgba(220,38,38,0)");
        cx.fillStyle = gf;
        cx.beginPath();
        cx.arc(0, 0, s, 0, 7);
        cx.fill();
        break;
      }
    }
    cx.restore();
  }

  /** En vol, elle tourne et montre sa tranche ; posée au sol, vue à plat. */
  private piece(p: Particule, s: number) {
    const cx = this.cx!;
    if (p.pose) cx.scale(1, 0.38);
    const w = p.pose ? 1 : Math.max(0.08, Math.abs(Math.cos(p.rot)));
    const tranche = p.pose ? s * 0.5 : Math.sign(Math.cos(p.rot)) * s * 0.22 * (1 - w);
    cx.fillStyle = "#92400e";
    cx.beginPath();
    cx.ellipse(p.pose ? 0 : tranche, p.pose ? tranche : 0, s * w, s, 0, 0, 7);
    cx.fill();
    const gr = cx.createLinearGradient(-s * w, -s, s * w, s);
    gr.addColorStop(0, "#fffbe0");
    gr.addColorStop(0.35, "#fcd34d");
    gr.addColorStop(0.7, "#f59e0b");
    gr.addColorStop(1, "#b45309");
    cx.fillStyle = gr;
    cx.beginPath();
    cx.ellipse(0, 0, s * w, s, 0, 0, 7);
    cx.fill();
    cx.strokeStyle = "rgba(120,53,15,.55)";
    cx.lineWidth = 1.3;
    cx.beginPath();
    cx.ellipse(0, 0, s * w * 0.76, s * 0.76, 0, 0, 7);
    cx.stroke();
    cx.fillStyle = "rgba(255,255,255,.55)";
    cx.beginPath();
    cx.ellipse(-s * 0.35 * w, -s * 0.42, s * 0.22 * w, s * 0.12, -0.5, 0, 7);
    cx.fill();
  }

  private arrondi(x: number, y: number, w: number, h: number, r: number) {
    const cx = this.cx!;
    cx.beginPath();
    cx.moveTo(x + r, y);
    cx.arcTo(x + w, y, x + w, y + h, r);
    cx.arcTo(x + w, y + h, x, y + h, r);
    cx.arcTo(x, y + h, x, y, r);
    cx.arcTo(x, y, x + w, y, r);
    cx.closePath();
  }

  eclat(x: number, y: number, n: number, o: { couleur?: string; vmin?: number; vmax?: number; g?: number } = {}) {
    for (let i = 0; i < n; i++) {
      const a = Math.random() * Math.PI * 2, v = hasard(o.vmin ?? 150, o.vmax ?? 420);
      this.ajoute({ type: "etincelle", x, y, vx: Math.cos(a) * v, vy: Math.sin(a) * v, g: o.g ?? 200, frein: 1.4, vie: hasard(0.7, 1.4), taille: hasard(1.5, 3.5), couleur: o.couleur ?? choisir(COULEURS) });
    }
  }

  /** Une pièce qui tombe, rebondit en tintant, puis se pose sur le tas qui monte. */
  pieceQuiTombe(x: number, y: number, vx: number, vy: number, k = 1) {
    this.ajoute({
      type: "piece", x, y, vx, vy, g: 1400, vie: hasard(5.5, 7), taille: hasard(10, 15), rot: Math.random() * 6, vr: hasard(7, 16), rebonds: 0,
      maj: (p) => {
        if (p.pose) {
          if (Math.random() < 0.004) this.ajoute({ type: "eclat", x: p.x + hasard(-6, 6), y: p.y - 2, g: 0, vie: 0.5, taille: 9 });
          return;
        }
        const col = Math.round(p.x / 18), sol = this.H - 8 - (this.tas[col] ?? 0);
        if (p.y >= sol && p.vy > 0) {
          p.y = sol;
          p.rebonds = (p.rebonds ?? 0) + 1;
          this.son.clink(k);
          if (p.rebonds >= 2 || Math.abs(p.vy) < 180) {
            p.pose = true;
            p.vx = p.vy = p.g = p.vr = 0;
            this.tas[col] = Math.min(this.H * 0.22, (this.tas[col] ?? 0) + 3.2);
          } else {
            p.vy *= -0.38;
            p.vx *= 0.55;
          }
        }
      },
    });
  }

  fontaine(x: number, y: number, n: number, k = 1) {
    for (let i = 0; i < n; i++) this.pieceQuiTombe(x + hasard(-10, 10), y, hasard(-420, 420), hasard(-1500, -1000), k);
  }
}

/* ═════════════ Les animations ═════════════ */

interface Reglage {
  euros: number;
  combo: number;
  intensite: number;
  /** Le son monte d'un ton à chaque étage du combo. */
  k: number;
}
interface Suite {
  /** Quand l'objet arrive (ms). */
  apres: number;
  titre: string;
  fondIntro?: boolean;
}

const ANIMATIONS: Record<Animation, (s: Scene, o: Reglage) => Suite> = {
  confettis(s, o) {
    s.voile();
    const n = Math.round(70 * o.intensite);
    ([[0, 1], [s.W, -1]] as const).forEach(([x0, sens], i) =>
      s.plus(i * 130, () => {
        for (let j = 0; j < n; j++) {
          const type = Math.random() < 0.18 ? "serpentin" : "confetti";
          s.ajoute({ type, x: x0, y: s.H, vx: sens * hasard(250, 850), vy: hasard(-1350, -700), g: 900, frein: type === "serpentin" ? 2.2 : 1.5, vie: hasard(2.6, 3.6), taille: hasard(8, 14), rot: Math.random() * 6, vr: hasard(-10, 10), couleur: choisir(COULEURS) });
        }
      }),
    );
    const t = s.son.t0;
    s.son.pop(t);
    s.son.pop(t + 0.13);
    [N.C5, N.E5, N.G5, N.C6, N.E6].forEach((f, i) => s.son.marimba(t + 0.22 + i * 0.075, f * o.k));
    return { apres: 220, titre: TITRES.confettis };
  },
  billets(s, o) {
    s.voile();
    const n = Math.round(45 * o.intensite);
    for (let i = 0; i < n; i++)
      s.plus(i * 22, () => s.ajoute({ type: "billet", x: hasard(0, s.W), y: -30, vy: hasard(60, 160), g: 260, frein: 1.8, vie: 4.2, rot: hasard(-0.6, 0.6), vr: hasard(-1.5, 1.5), maj: (p) => { p.vx = Math.sin(p.age * 2.6 + p.phase) * 110; } }));
    const t = s.son.t0;
    s.son.compteur(t, 0.85);
    s.son.caisse(t + 0.9, o.k);
    return { apres: 250, titre: TITRES.billets };
  },
  pluie(s, o) {
    s.voile();
    const n = Math.round(85 * o.intensite);
    for (let i = 0; i < n; i++) s.plus(i * 14, () => s.pieceQuiTombe(hasard(0, s.W), -20, hasard(-60, 60), hasard(100, 400), o.k));
    const t = s.son.t0;
    s.son.cloche(t, 1568 * o.k, 0.06, 1.6);
    s.son.cloche(t + 0.12, 2093 * o.k, 0.05, 1.8);
    return { apres: 300, titre: TITRES.pluie };
  },
  artifice(s, o) {
    s.voile("nuit");
    const nb = Math.round(5 * o.intensite);
    for (let i = 0; i < nb; i++)
      s.plus(i * 240, () => {
        s.son.sifflet(s.son.t0);
        const couleur = choisir(COULEURS), autre = choisir(COULEURS);
        s.ajoute({
          type: "fusee", x: hasard(s.W * 0.15, s.W * 0.85), y: s.H + 10, vx: hasard(-40, 40), vy: hasard(-1080, -860), g: 700, vie: hasard(0.8, 1.0),
          maj: (p) => { s.ajoute({ type: "etincelle", x: p.x, y: p.y, vx: hasard(-15, 15), vy: hasard(0, 50), g: 0, vie: 0.35, taille: 1.8, couleur: "#ffd27a" }); },
          fin: (p) => {
            s.eclat(p.x, p.y, 90, { couleur, vmin: 120, vmax: 380, g: 140 });
            s.eclat(p.x, p.y, 35, { couleur: autre, vmin: 40, vmax: 180, g: 140 });
            s.son.boum(s.son.t0);
          },
        });
      });
    return { apres: 1500, titre: TITRES.artifice };
  },
  strike(s, o) {
    s.voile();
    const cy = s.H * 0.5, base = s.W * 0.5 - 20, quilles: Particule[] = [];
    for (let r = 0; r < 4; r++) for (let i = 0; i <= r; i++) quilles.push(s.ajoute({ type: "quille", x: base + r * 30, y: cy + (i - r / 2) * 30, g: 0, vie: 9 }));
    const duree = 0.6;
    const boule = s.ajoute({ type: "boule", x: -40, y: cy, vx: (base + 40) / duree, g: 0, vie: 3, vr: 14 });
    s.son.roule(s.son.t0, duree);
    s.plus(duree * 1000, () => {
      for (const q of quilles) {
        q.vx = hasard(180, 700);
        q.vy = hasard(-750, -250);
        q.vr = hasard(-16, 16);
        q.g = 1300;
        q.vie = q.age + 1.8;
      }
      boule.vx *= 0.55;
      boule.vie = boule.age + 0.7;
      s.eclat(base, cy, 60, { couleur: "#fde68a" });
      s.secoue();
      s.flash();
      const t = s.son.t0;
      s.son.quilles(t);
      s.son.cymbale(t + 0.05, 0.16);
      s.plus(150, () => {
        s.choc("STRIKE !", "Les dix quilles", "rouge");
        s.son.fanfare(s.son.t0, FANFARE.courte, o.k);
      });
    });
    return { apres: 1800, titre: TITRES.strike, fondIntro: true };
  },
  jackpot(s, o) {
    s.voile();
    s.el.appendChild(s.div("fete-rayons"));
    const html = "JACKPOT".split("").map((c, j) => `<span style="animation-delay:${j * 0.08}s, ${0.8 + j * 0.08}s">${c}</span>`).join("");
    s.choc(html, "Grosse vente", "lettres");
    s.flash();
    s.secoue();
    const fin = performance.now() + 2400;
    const jet = () => {
      if (performance.now() > fin) return;
      s.fontaine(s.W / 2, s.H + 10, Math.round(3 * o.intensite), o.k);
      s.plus(45, jet);
    };
    jet();
    s.plus(900, () => s.secoue());
    const t = s.son.t0;
    [N.C6, N.E6, N.G6, N.C7, N.G6, N.C7].forEach((f, i) => s.son.cloche(t + i * 0.16, f * o.k, 0.05, 1.4));
    s.son.cymbale(t, 0.15);
    s.son.fanfare(t + 0.25, FANFARE.moyenne, o.k);
    return { apres: 2000, titre: TITRES.jackpot, fondIntro: true };
  },
  legendaire(s, o) {
    s.voile("noir");
    const t = s.son.t0;
    s.son.envol(t, 1.3);
    s.son.choeur(t + 0.2, [N.C5, N.E5, N.G5, N.C6], 2.6);
    s.plus(1100, () => {
      s.choc("LÉGENDAIRE", "Vente de légende", "arcenciel");
      s.flash();
      s.secoue();
      const t2 = s.son.t0;
      s.son.boum(t2, 0.6);
      s.son.cymbale(t2, 0.2);
      s.son.fanfare(t2 + 0.2, FANFARE.longue, o.k);
      for (let i = 0; i < 8; i++)
        s.plus(200 + i * 230, () => {
          s.eclat(hasard(s.W * 0.12, s.W * 0.88), hasard(s.H * 0.1, s.H * 0.45), 90, { couleur: choisir(COULEURS), vmin: 120, vmax: 380, g: 120 });
          s.son.boum(s.son.t0, 0.35);
        });
      for (let j = 0; j < 90; j++) s.plus(300 + j * 16, () => s.pieceQuiTombe(hasard(0, s.W), -20, hasard(-50, 50), hasard(120, 380), o.k));
    });
    return { apres: 3300, titre: TITRES.legendaire, fondIntro: true };
  },
};

/* ═════════════ L'objet de la récompense ═════════════ */

/** `couleur` : celle de l'impact (onde, éclats) — le bouton, lui, est celui de la librairie, toujours le même. */
const OBJETS: Record<Animation, { couleur: string; atterrit: number; projecteurs?: boolean }> = {
  confettis: { couleur: "#db2777", atterrit: 420 },
  billets: { couleur: "#16a34a", atterrit: 900 },
  pluie: { couleur: "#d97706", atterrit: 850 },
  artifice: { couleur: "#7c3aed", atterrit: 350 },
  strike: { couleur: "#dc2626", atterrit: 480, projecteurs: true },
  jackpot: { couleur: "#fbbf24", atterrit: 650, projecteurs: true },
  legendaire: { couleur: "#e9d5ff", atterrit: 1000, projecteurs: true },
};

/** Les classes de `Button` (Button.css) posées sur les boutons du moteur, qui n'est pas du React :
 *  le CTA = `primary` (l'encre du thème), la sortie = `ghost`. */
const BTN_CTA = "fete-vivant btn-look btn-pilule btn-verre btn-teinte-neutre";
const BTN_SORTIE = "fete-fermer btn-look btn-pilule btn-verre btn-teinte-fantome";

function dateDuJour(): string {
  const d = new Date();
  return [d.getDate(), d.getMonth() + 1].map((n) => String(n).padStart(2, "0")).join("-") + "-" + d.getFullYear();
}

function htmlObjet(a: Animation, d: DonneesVente, titre: string): string {
  const montant = '<div class="fete-montant" data-testid="vente-celebration-montant">+0 ' + echapper(d.devise) + "</div>";
  const somme = echapper((Number.isInteger(d.euros) ? String(d.euros) : d.euros.toFixed(2)) + " " + d.devise);
  const etiquette = '<span class="fete-etiquette">' + echapper(titre) + "</span>";
  switch (a) {
    case "billets":
      return `<div class="o-billet"><div class="cadre"></div><span class="coin hg">${somme}</span><span class="banque">Banque Cyrano</span><span class="serie">N° ${Math.floor(hasard(1e6, 9e6))}</span><span class="coin bd">${somme}</span><div class="ovale">${etiquette}${montant}</div></div>`;
    case "pluie":
      return `<div class="o-piece"><div class="face">${etiquette}${montant}<span class="etoiles">★★★</span></div></div>`;
    case "artifice":
      return `<div class="o-ciel">${etiquette}${montant}</div>`;
    case "strike": {
      const x = Math.max(1, Math.min(10, d.combo));
      let cases = "";
      for (let i = 1; i <= 10; i++) cases += `<div class="case${i <= x ? " x" : ""}" style="--i:${i}"><small>${i}</small><b>${i <= x ? "X" : "·"}</b></div>`;
      return `<div class="o-tableau"><div class="haut"><span class="fete-etiquette clignote">${x > 1 ? x + " strikes d'affilée" : "Strike"}</span><span class="fete-etiquette">Piste 7</span></div><div class="cases">${cases}</div><div class="ecran">${montant}</div></div>`;
    }
    case "jackpot":
      return `<div class="ombre-or"><div class="o-lingot"><span class="grave">Jackpot · or fin 999,9</span>${montant}<span class="grave">Cyrano · ${dateDuJour()}</span></div></div>`;
    case "legendaire":
      return `<div class="o-collection"><div class="interieur">${etiquette}<span class="rarete">★★★★★</span><div class="joyau">${ICONES.diamant}</div>${montant}<span class="edition">Édition unique · ${dateDuJour()}</span></div></div>`;
    default:
      return `<div class="ombre"><div class="o-ticket"><div class="principal">${etiquette}${montant}</div><div class="souche"><span>Vente du ${dateDuJour()}</span></div></div></div>`;
  }
}

function recompense(s: Scene, a: Animation, d: DonneesVente, titre: string, o: Reglage, lib: Libelles, rappels: Rappels) {
  const obj = OBJETS[a];
  if (obj.projecteurs && !reduit()) s.projecteurs();
  const rec = s.div("fete-recompense");
  rec.setAttribute("data-testid", "vente-celebration-carte");
  const infos: string[] = [];
  const qui = [d.fan ? lib.aQui(d.fan) : null, d.createur].filter(Boolean).join(" · ");
  if (qui) infos.push(`<div class="aqui" data-testid="vente-celebration-fan">${echapper(qui)}</div>`);
  if (d.quoi) infos.push(`<div class="meta">${echapper(d.quoi)}</div>`);
  if (d.venteDuJour !== null && d.totalDuJour !== null)
    infos.push(`<div class="meta">${echapper(lib.venteDuJour(d.venteDuJour))} · ${echapper(lib.totalDuJour(d.totalDuJour))}</div>`);
  rec.innerHTML =
    htmlObjet(a, d, titre) +
    `<div class="fete-infos">${infos.join("")}</div>` +
    '<div class="fete-actions">' +
    (d.peutOuvrir
      ? `<button type="button" class="${BTN_CTA}">${echapper(lib.ouvrir)} ${ICONES.fleche}</button>`
      : "") +
    `<button type="button" class="${BTN_SORTIE}">${echapper(lib.fermer)}</button></div>`;
  s.el.appendChild(rec);
  rec.querySelector<HTMLButtonElement>(".fete-vivant")?.addEventListener("click", () => {
    rappels.onOuvrir();
    s.fermer();
  });
  rec.querySelector<HTMLButtonElement>(".fete-fermer")!.addEventListener("click", s.fermer);

  // L'impact quand l'objet touche la scène — le geste du podium.
  s.plus(obj.atterrit, () => {
    const z = (rec.firstElementChild as HTMLElement).getBoundingClientRect(), x0 = z.left + z.width / 2, y0 = z.top + z.height / 2;
    const couleur = obj.couleur;
    const onde = s.div("fete-onde");
    onde.style.left = x0 - 90 + "px";
    onde.style.top = y0 - 90 + "px";
    onde.style.borderColor = couleur;
    s.el.insertBefore(onde, rec);
    s.eclat(x0, y0, 70, { couleur, vmin: 220, vmax: 560, g: 260 });
    s.eclat(x0, y0, 30, { couleur: "#fff", vmin: 120, vmax: 320, g: 260 });
    const petit = a === "confettis" || a === "artifice";
    s.son.boum(s.son.t0, petit ? 0.25 : 0.45);
    if (!petit) {
      s.secoue();
      s.flash();
    }
  });

  // Le montant qui grimpe, avec son cliquetis.
  const m = rec.querySelector<HTMLElement>(".fete-montant")!;
  const cible = d.euros, entier = Number.isInteger(cible), duree = 1100 + Math.min(1400, cible * 6), retard = a === "pluie" || a === "legendaire" ? 900 : 350;
  const t = s.son.t0, nbTics = Math.round(duree / 60);
  for (let i = 0; i < nbTics; i++) s.son.tic(t + retard / 1000 + i * (duree / 1000 / nbTics), 1 + (i / nbTics) * 0.5);
  const ecrire = (v: number) => {
    m.textContent = "+" + (entier ? Math.round(v) : v.toFixed(2)) + " " + d.devise;
  };
  if (reduit()) ecrire(cible);
  else {
    let debut: number | null = null;
    s.plus(retard, () =>
      requestAnimationFrame(function compte(ts) {
        if (!m.isConnected) return;
        if (debut === null) debut = ts;
        const p = Math.min(1, (ts - debut) / duree);
        ecrire(cible * (1 - Math.pow(1 - p, 3)));
        if (p < 1) requestAnimationFrame(compte);
        else {
          m.classList.add("fini");
          s.son.ding(s.son.t0, o.k);
          const rr = m.getBoundingClientRect();
          s.eclat(rr.left + rr.width / 2, rr.top + rr.height / 2, 34, { couleur: "#fde68a", vmin: 80, vmax: 260, g: 0 });
        }
      }),
    );
  }
}

function bandeauCombo(s: Scene, n: number) {
  if (n < 2) return;
  // Le compteur est un `Badge` ambre de la librairie (`bd lc-pilule`, lecture.css) : un combo, c'est de l'attention.
  const b = s.div("fete-combo", `<span class="bd lc-pilule" data-ton="ambre" data-taille="md">COMBO ×${n}</span><span class="nom">${nomCombo(n)}</span>`);
  s.el.appendChild(b);
  s.plus(200, () => {
    const r = b.getBoundingClientRect(), fin = performance.now() + 1800;
    const feu = () => {
      if (performance.now() > fin || !b.isConnected) return;
      for (let i = 0; i < Math.min(6, n); i++)
        s.ajoute({ type: "flamme", x: hasard(r.left + 10, r.right - 10), y: r.bottom - 6, vx: hasard(-20, 20), vy: hasard(-160, -60), g: -60, vie: hasard(0.5, 0.9), taille: hasard(8, 16) });
      s.plus(50, feu);
    };
    feu();
  });
}

/* ═════════════ Les deux entrées publiques ═════════════ */

/** Joue la fête d'une vente dans `racine`. Rend la fonction qui la démonte. */
export function lancerVente(racine: HTMLElement, d: DonneesVente, sonActif: boolean, lib: Libelles, rappels: Rappels): () => void {
  return quandLaScene(() => venteMaintenant(racine, d, sonActif, lib, rappels));
}

function venteMaintenant(racine: HTMLElement, d: DonneesVente, sonActif: boolean, lib: Libelles, rappels: Rappels): () => void {
  const s = new Scene(racine, sonActif, rappels.onFermer);
  sceneActive = s;
  const a = animationDe(d.euros);
  const p = PALIERS.find((x) => d.euros >= x.de && d.euros < x.a) ?? PALIERS[0]!;
  const dansLePalier = p.a === Infinity ? 1 : (d.euros - p.de) / (p.a - p.de);
  const combo = Math.max(1, d.combo);
  const o: Reglage = {
    euros: d.euros,
    combo,
    intensite: (1 + dansLePalier * 0.4) * (1 + 0.2 * Math.min(combo - 1, 5)),
    k: Math.pow(2, (Math.min(combo - 1, 6) * 2) / 12),
  };
  if (reduit()) {
    s.voile();
    recompense(s, a, d, "Vendu", o, lib, rappels);
  } else {
    const suite = ANIMATIONS[a](s, o);
    bandeauCombo(s, combo);
    s.plus(suite.apres, () => {
      if (suite.fondIntro)
        s.el.querySelectorAll<HTMLElement>(".fete-intro").forEach((i) => {
          i.classList.add("part");
          window.setTimeout(() => i.remove(), 300);
        });
      s.son.bruit(s.son.t0, 0.35, { f: 300, fVers: 3500, q: 1.2, g: 0.16 });
      recompense(s, a, d, suite.titre, o, lib, rappels);
    });
  }
  s.plus(14_000, s.fermer);
  return () => s.detruire();
}

const METAUX: Record<1 | 2 | 3, string[]> = {
  1: ["#DEC480", "#fff7c2", "#a8893f"],
  2: ["#C4CAD4", "#ffffff", "#7c8594"],
  3: ["#CC9670", "#fde2cf", "#8a5a37"],
};

/** La montée sur le podium de la semaine : roulement de tambour, projecteurs,
 *  la médaille qui tombe en tournant, et pour le n°1 la couronne. */
export function lancerPodium(racine: HTMLElement, d: DonneesPodium, sonActif: boolean, rappels: Rappels): () => void {
  return quandLaScene(() => podiumMaintenant(racine, d, sonActif, rappels));
}

function podiumMaintenant(racine: HTMLElement, d: DonneesPodium, sonActif: boolean, rappels: Rappels): () => void {
  const s = new Scene(racine, sonActif, rappels.onFermer);
  sceneActive = s;
  const { rang, libelles: lib } = d;
  s.voile("noir");
  s.projecteurs();
  const bloc = s.div("fete-podium");
  bloc.setAttribute("data-testid", "rang-podium");
  s.el.appendChild(bloc);
  bloc.appendChild(s.div("surtitre", echapper(lib.surtitre)));
  const roulement = reduit() ? 0 : rang === 1 ? 1.8 : 1.4;
  s.son.roulement(s.son.t0, roulement);
  s.plus(roulement * 1000, () => {
    const med = s.div("fete-medaille m-" + rang + (reduit() ? "" : " tombe"), `<div class="ruban"></div><div class="disque">${rang}</div>`);
    bloc.appendChild(med);
    s.plus(reduit() ? 0 : 820, () => {
      s.flash();
      s.secoue();
      s.son.cymbale(s.son.t0, 0.22);
      const r = med.querySelector(".disque")!.getBoundingClientRect(), x0 = r.left + r.width / 2, y0 = r.top + r.height / 2;
      const onde = s.div("fete-onde");
      onde.style.left = x0 - 90 + "px";
      onde.style.top = y0 - 90 + "px";
      s.el.appendChild(onde);
      const metal = METAUX[rang];
      s.eclat(x0, y0, 110, { couleur: metal[0], vmin: 200, vmax: 600, g: 300 });
      s.son.boum(s.son.t0, 0.5);
      s.son.fanfare(s.son.t0 + 0.1, rang === 1 ? FANFARE.longue : FANFARE.moyenne, rang === 1 ? 1 : rang === 2 ? 0.94 : 0.89);
      if (rang === 1) s.son.choeur(s.son.t0 + 0.9, [N.C5, N.E5, N.G5, N.C6], 2.8);
      const nb = rang === 1 ? 260 : rang === 2 ? 160 : 110;
      const couleurs = rang === 1 ? [...metal, "#ffffff"] : metal;
      for (let i = 0; i < nb; i++)
        s.plus(i * 12, () => s.ajoute({ type: "ruban", x: hasard(0, s.W), y: -20, vy: hasard(80, 220), g: 120, frein: 0.6, vie: 5, taille: hasard(8, 14), rot: hasard(-0.5, 0.5), vr: hasard(-3, 3), couleur: choisir(couleurs) }));
      if (rang <= 2)
        for (let j = 0; j < (rang === 1 ? 9 : 4); j++)
          s.plus(500 + j * 320, () => {
            s.eclat(hasard(s.W * 0.1, s.W * 0.9), hasard(s.H * 0.08, s.H * 0.4), 90, { couleur: choisir(rang === 1 ? ["#DEC480", "#fff7c2", "#f59e0b", "#fff"] : COULEURS), vmin: 120, vmax: 380, g: 120 });
            s.son.boum(s.son.t0, 0.3);
          });
      if (rang === 1)
        s.plus(700, () => {
          med.appendChild(s.div("couronne", '<svg viewBox="0 0 24 24" fill="#facc15" stroke="#a16207" stroke-width="1.2" stroke-linejoin="round"><path d="M3 8l4.5 4L12 5l4.5 7L21 8l-2 11H5z"/></svg>'));
          s.plus(450, () => {
            s.son.cloche(s.son.t0, 2093, 0.08, 2);
            s.fontaine(s.W / 2, s.H + 10, 40);
          });
        });
      bloc.appendChild(s.div("fete-choc metal-" + rang, rang === 1 ? "N°1 !" : "TOP " + rang + " !"));
      s.plus(500, () => {
        if (d.devant) bloc.appendChild(s.div("devant", echapper(lib.devant(d.devant))));
        if (d.prime) {
          // La prime : un `Badge` ambre de la librairie.
          const prime = s.div("prime bd lc-pilule", echapper(lib.prime(d.prime)));
          prime.dataset.ton = "ambre";
          prime.dataset.taille = "md";
          bloc.appendChild(prime);
        }
        const voir = document.createElement("button");
        voir.type = "button";
        voir.className = BTN_CTA;
        voir.innerHTML = echapper(lib.voir) + " " + ICONES.fleche;
        voir.onclick = () => {
          rappels.onOuvrir();
          s.fermer();
        };
        const non = document.createElement("button");
        non.type = "button";
        non.className = BTN_SORTIE;
        non.textContent = lib.fermer;
        non.onclick = s.fermer;
        bloc.appendChild(voir);
        bloc.appendChild(non);
      });
    });
  });
  s.plus(14_000, s.fermer);
  return () => s.detruire();
}

/* ═════════════ La petite fête : la vente des AUTRES, en bas à droite ═════════════ */

export interface DonneesMini {
  euros: number;
  devise: string;
  /** « Léa a vendu », « Cyrano a vendu », « Tu as vendu ! » — déjà résolu. */
  qui: string;
  fan: string | null;
  createur: string | null;
}

/** Échelle de chaque objet pour tenir dans la vignette (94 × 80). */
const ECHELLE: Record<Animation, number> = { confettis: 0.23, billets: 0.2, pluie: 0.28, artifice: 0.17, strike: 0.18, jackpot: 0.2, legendaire: 0.2 };

/** Les éclats qui sortent de l'objet, aux couleurs de l'animation. */
const ECLATS: Record<Animation, { forme: string; couleurs: string[] }> = {
  confettis: { forme: "", couleurs: COULEURS },
  billets: { forme: "billet", couleurs: ["#16a34a", "#22c55e", "#86efac"] },
  pluie: { forme: "rond", couleurs: ["#fbbf24", "#f59e0b", "#fde68a"] },
  artifice: { forme: "lueur", couleurs: ["#f472b6", "#a855f7", "#facc15", "#22d3ee"] },
  strike: { forme: "lueur", couleurs: ["#fdba74", "#fb923c", "#fff"] },
  jackpot: { forme: "rond", couleurs: ["#fbbf24", "#fde68a", "#f59e0b"] },
  legendaire: { forme: "lueur", couleurs: ["#f9a8d4", "#c4b5fd", "#93c5fd", "#86efac", "#fde68a"] },
};

/** Le son de la petite fête : la même signature que la grande, en court. */
function petitSon(son: Son, a: Animation) {
  const t = son.t0 + 0.15;
  switch (a) {
    case "confettis":
      son.pop(t);
      [N.C5, N.E5, N.G5].forEach((f, i) => son.marimba(t + 0.1 + i * 0.07, f));
      break;
    case "billets":
      son.caisse(t);
      break;
    case "pluie":
      for (let i = 0; i < 4; i++) son.metal(t + i * 0.11, hasard(2600, 3400), 0.05, 0.4);
      break;
    case "artifice":
      son.sifflet(t);
      son.boum(t + 0.7, 0.3);
      break;
    case "strike":
      son.quilles(t);
      break;
    case "jackpot":
      [N.C6, N.E6, N.G6].forEach((f, i) => son.cloche(t + i * 0.14, f, 0.05, 1.2));
      for (let i = 0; i < 3; i++) son.metal(t + 0.45 + i * 0.1, hasard(2600, 3400), 0.04, 0.35);
      break;
    case "legendaire":
      son.envol(t, 0.8);
      son.cloche(t + 0.6, 1568, 0.07, 1.6);
      break;
  }
}

/**
 * Remplit `racine` (la carte du coin, tenue par React) avec la petite fête :
 * l'objet de l'animation en miniature, ses éclats, le montant qui grimpe et un
 * son court. Rend la fonction qui la démonte.
 */
export function lancerMiniVente(
  racine: HTMLElement,
  d: DonneesMini,
  son: { actif: boolean; volume: number },
  lib: { fermer: string; aQui: (fan: string) => string },
  rappels: { onOuvrir: (() => void) | null; onFermer: () => void },
): () => void {
  const a = animationDe(d.euros);
  const objet = htmlObjet(a, { euros: d.euros, devise: d.devise, fan: null, createur: null, quoi: null, combo: 1, venteDuJour: null, totalDuJour: null, peutOuvrir: false }, TITRES[a]);
  const eclats = reduit()
    ? ""
    : Array.from({ length: 16 }, () => {
        const e = ECLATS[a];
        const st = `--c:${choisir(e.couleurs)};--dx:${Math.round(hasard(-85, 85))}px;--dy:${Math.round(hasard(-95, 25))}px;--r:${Math.round(hasard(-300, 300))}deg;--d:${(0.35 + Math.random() * 0.25).toFixed(2)}s`;
        return `<span class="mini-p ${e.forme}" style="${st}"></span>`;
      }).join("");
  const meta = [d.fan ? lib.aQui(d.fan) : null, d.createur].filter(Boolean).join(" · ");

  const carte = document.createElement("div");
  // `dark` : le verre de la carte est celui de la nuit, quel que soit le thème de la page.
  carte.className = "fete fete-mini dark" + (rappels.onOuvrir ? " ouvrable" : "");
  carte.innerHTML =
    `<div class="mini-objet"><div class="mini-echelle" style="--k:${ECHELLE[a]}">${objet}</div>${eclats}</div>` +
    `<div class="mini-texte"><span class="mini-titre">${echapper(TITRES[a])}</span><span class="mini-qui">${echapper(d.qui)}</span>` +
    `<span class="mini-montant">+0 ${echapper(d.devise)}</span>` +
    (meta ? `<span class="mini-meta">${echapper(meta)}</span>` : "") +
    `</div><button type="button" class="mini-fermer btn-look btn-pilule btn-verre btn-teinte-fantome" aria-label="${echapper(lib.fermer)}">×</button>`;
  racine.appendChild(carte);

  const minuteries: number[] = [];
  const s = new Son(son.actif, son.volume);
  petitSon(s, a);
  let fini = false;
  const fermer = () => {
    if (fini) return;
    fini = true;
    carte.classList.add("part");
    minuteries.push(window.setTimeout(rappels.onFermer, 300));
  };
  if (rappels.onOuvrir) {
    const ouvrir = rappels.onOuvrir;
    carte.addEventListener("click", () => {
      ouvrir();
      fermer();
    });
  }
  carte.querySelector<HTMLButtonElement>(".mini-fermer")!.addEventListener("click", (e) => {
    e.stopPropagation();
    fermer();
  });

  // Le montant qui grimpe, dans l'objet et dans le texte.
  const montants = Array.from(carte.querySelectorAll<HTMLElement>(".fete-montant, .mini-montant"));
  const cible = d.euros, entier = Number.isInteger(cible);
  const ecrire = (v: number) => montants.forEach((m) => (m.textContent = "+" + (entier ? Math.round(v) : v.toFixed(2)) + " " + d.devise));
  let raf: number | null = null;
  if (reduit()) ecrire(cible);
  else {
    let debut: number | null = null;
    minuteries.push(
      window.setTimeout(() => {
        raf = requestAnimationFrame(function compte(ts) {
          if (debut === null) debut = ts;
          const p = Math.min(1, (ts - debut) / 900);
          ecrire(cible * (1 - Math.pow(1 - p, 3)));
          raf = p < 1 ? requestAnimationFrame(compte) : null;
        });
      }, 300),
    );
  }
  minuteries.push(window.setTimeout(fermer, 8000));

  return () => {
    minuteries.forEach(clearTimeout);
    if (raf !== null) cancelAnimationFrame(raf);
    s.couper();
    carte.remove();
  };
}
